/**
 * Presentation parser for agent-authored messages.
 *
 * Agents often return structured deliverables as:
 *   - raw JSON strings
 *   - fenced markdown blocks (```json ... ```)
 *   - prose followed by a fenced JSON block
 *
 * The reducer intentionally keeps the original text untouched for audit and
 * replay. This utility derives display blocks so UI components can render a
 * human-readable view without mutating the source event.
 */

function asText(value) {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function tryParseJson(raw) {
  const s = asText(raw).trim();
  if (!s) return null;
  const first = s[0];
  if (first !== '{' && first !== '[') return null;
  try {
    return { value: JSON.parse(s), raw: s };
  } catch {
    return null;
  }
}

function unquoteJsonString(raw) {
  const s = asText(raw);
  try {
    return JSON.parse(s);
  } catch {
    return s
      .replace(/^"/, '')
      .replace(/"$/, '')
      .replace(/\\n/g, '\n')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
  }
}

function findStringEnd(s, start) {
  let escaped = false;
  for (let i = start + 1; i < s.length; i += 1) {
    const ch = s[i];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (ch === '\\') {
      escaped = true;
      continue;
    }
    if (ch === '"') return i;
  }
  return -1;
}

function findBalancedEnd(s, start, open, close) {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < s.length; i += 1) {
    const ch = s[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === open) depth += 1;
    if (ch === close) {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function parseLooseArray(raw) {
  const exact = tryParseJson(raw);
  if (exact) return exact.value;
  const items = [];
  const s = asText(raw);
  const stringRe = /"((?:\\.|[^"\\])*)"/g;
  let m;
  while ((m = stringRe.exec(s)) !== null) {
    items.push(unquoteJsonString(`"${m[1]}"`));
  }
  if (items.length) return items;
  const single = s.replace(/^\[/, '').replace(/\]$/, '').trim();
  if (!single) return [];
  // Common model truncation: `"items": [ "First item` without a closing
  // quote/bracket. Treat the visible item as one bullet instead of showing
  // a raw `"First item` string in the UI.
  return [unquoteJsonString(single.replace(/,$/, '').trim())];
}

/**
 * Best-effort parser for LLM JSON-ish objects.
 *
 * This intentionally handles only top-level `"key": value` pairs. It is not a
 * replacement for JSON.parse; it is a presentation fallback for common model
 * failure modes such as a truncated final array or an unclosed fenced block.
 */
export function parseLooseJsonObject(raw) {
  const s = asText(raw).trim();
  if (!s.startsWith('{')) return null;
  const out = {};
  let i = 1;
  while (i < s.length) {
    while (i < s.length && /[\s,}]/.test(s[i])) i += 1;
    if (s[i] !== '"') {
      i += 1;
      continue;
    }
    const keyEnd = findStringEnd(s, i);
    if (keyEnd === -1) break;
    const key = unquoteJsonString(s.slice(i, keyEnd + 1));
    i = keyEnd + 1;
    while (i < s.length && /\s/.test(s[i])) i += 1;
    if (s[i] !== ':') continue;
    i += 1;
    while (i < s.length && /\s/.test(s[i])) i += 1;

    const valueStart = i;
    const ch = s[i];
    let value;
    if (ch === '"') {
      const valueEnd = findStringEnd(s, i);
      if (valueEnd === -1) break;
      value = unquoteJsonString(s.slice(i, valueEnd + 1));
      i = valueEnd + 1;
    } else if (ch === '[') {
      const arrEnd = findBalancedEnd(s, i, '[', ']');
      const arrRaw = arrEnd === -1 ? s.slice(i) : s.slice(i, arrEnd + 1);
      value = parseLooseArray(arrRaw);
      i = arrEnd === -1 ? s.length : arrEnd + 1;
    } else if (ch === '{') {
      const objEnd = findBalancedEnd(s, i, '{', '}');
      const objRaw = objEnd === -1 ? s.slice(i) : s.slice(i, objEnd + 1);
      value = tryParseJson(objRaw)?.value ?? parseLooseJsonObject(objRaw) ?? objRaw;
      i = objEnd === -1 ? s.length : objEnd + 1;
    } else {
      while (i < s.length && s[i] !== ',' && s[i] !== '}') i += 1;
      const primitive = s.slice(valueStart, i).trim();
      if (/^-?\d+(\.\d+)?$/.test(primitive)) value = Number(primitive);
      else if (primitive === 'true') value = true;
      else if (primitive === 'false') value = false;
      else if (primitive === 'null') value = null;
      else value = primitive;
    }

    if (key) out[key] = value;
  }
  return Object.keys(out).length ? out : null;
}

function pushTextBlock(blocks, text) {
  const t = asText(text).trim();
  if (t) blocks.push({ type: 'text', text: t });
}

function blockFromCode(language, code) {
  const raw = asText(code).trim();
  const parsed = tryParseJson(raw);
  if (parsed) {
    return {
      type: 'json',
      value: parsed.value,
      raw: parsed.raw,
      language: language || 'json',
    };
  }
  const loose = parseLooseJsonObject(raw);
  if (loose) {
    return {
      type: 'json',
      value: loose,
      raw,
      language: language || 'json',
      partial: true,
    };
  }
  return {
    type: 'code',
    language: language || 'text',
    text: raw,
  };
}

/**
 * @param {unknown} input
 * @returns {Array<
 *   | { type: 'text', text: string }
 *   | { type: 'json', value: unknown, raw: string, language?: string }
 *   | { type: 'code', text: string, language?: string }
 * >}
 */
export function parseAgentMessageBlocks(input) {
  const text = asText(input);
  const trimmed = text.trim();
  if (!trimmed) return [];

  const fullJson = tryParseJson(trimmed);
  if (fullJson) {
    return [{ type: 'json', value: fullJson.value, raw: fullJson.raw, language: 'json' }];
  }
  const looseJson = parseLooseJsonObject(trimmed);
  if (looseJson) {
    return [{ type: 'json', value: looseJson, raw: trimmed, language: 'json', partial: true }];
  }

  const blocks = [];
  const fenceRe = /```([A-Za-z0-9_-]+)?[ \t]*\n?([\s\S]*?)(?:```|$)/g;
  let last = 0;
  let matched = false;
  let m;
  while ((m = fenceRe.exec(text)) !== null) {
    matched = true;
    pushTextBlock(blocks, text.slice(last, m.index));
    const language = (m[1] || '').trim().toLowerCase();
    const code = m[2] || '';
    const block = blockFromCode(language, code);
    if (block.type !== 'code' || block.text) blocks.push(block);
    last = fenceRe.lastIndex;
  }
  if (matched) {
    pushTextBlock(blocks, text.slice(last));
    return blocks;
  }

  return [{ type: 'text', text: trimmed }];
}

export function humanizeJsonKey(key) {
  return String(key || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}

/**
 * Split lightweight markdown bold markers into inline display segments.
 *
 * We intentionally support only ``**bold**`` here. Agent responses are not
 * trusted markdown documents, and rendering full markdown would increase the
 * XSS/sanitization surface. React escapes the returned text, so this remains a
 * safe presentation-only transform for common LLM output like
 * ``**Cost Control**``.
 */
export function parseBoldSegments(input) {
  const text = asText(input);
  if (!text.includes('**')) return [{ text, bold: false }];
  const out = [];
  const re = /\*\*([^*]+?)\*\*/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ text: text.slice(last, m.index), bold: false });
    out.push({ text: m[1], bold: true });
    last = re.lastIndex;
  }
  if (last < text.length) out.push({ text: text.slice(last), bold: false });
  return out.length ? out : [{ text, bold: false }];
}

function isPlainPrimitive(v) {
  return v == null || ['string', 'number', 'boolean'].includes(typeof v);
}

function stripBoldMarkersForCopy(s) {
  if (!s) return '';
  return parseBoldSegments(s)
    .map((seg) => seg.text)
    .join('');
}

function primitiveToCopyLine(value) {
  if (value == null) return 'Not specified';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return stripBoldMarkersForCopy(value);
  return String(value);
}

/**
 * Renders a parsed agent message to plain text for clipboard, aligned with
 * `AgentMessageContent` (bold stripped, humanized object keys, list bullets).
 * @param {unknown} value
 * @returns {string}
 */
function jsonValueToCopyText(value) {
  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';
    if (value.every((v) => isPlainPrimitive(v))) {
      return value
        .map((item) => {
          const line = primitiveToCopyLine(item);
          return `• ${line.split('\n').join('\n  ')}`;
        })
        .join('\n');
    }
    return value
      .map((item) => jsonValueToCopyText(item))
      .filter((t) => t && t.trim())
      .join('\n\n');
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return 'No details';
    return entries
      .map(([k, child]) => {
        const label = humanizeJsonKey(k);
        const body = jsonValueToCopyText(child);
        return `${label}\n${body}`;
      })
      .join('\n\n');
  }

  return primitiveToCopyLine(value);
}

/**
 * Best-effort plain text for the clipboard: same parsing as the UI, without
 * markdown `**` markers and with readable labels for JSON objects.
 * @param {unknown} input
 * @returns {string}
 */
export function formatAgentMessageForCopy(input) {
  const blocks = parseAgentMessageBlocks(input);
  const out = [];
  for (const block of blocks) {
    if (block.type === 'text') {
      const t = stripBoldMarkersForCopy((block.text || '').trim());
      if (t) out.push(t);
    } else if (block.type === 'code') {
      const t = (block.text || '').trim();
      if (t) out.push(t);
    } else if (block.type === 'json') {
      out.push(jsonValueToCopyText(block.value));
    }
  }
  return out.filter((s) => s && s.trim()).join('\n\n').trim();
}
